import React, { Fragment, SetStateAction, useEffect, useRef, useState } from "react";
import styles from "./Entity.module.scss";
import { Formik, Form, FormikProps, FormikValues, useFormikContext } from "formik";
import api, { useFetch, ErrorMessage } from "api";
import { ObjectSchema } from "yup";
import { AxiosRequestConfig } from "axios";
import { createSubmitForm, createMultipartSubmitForm } from "helpers/submitForm";
import { sanitizeConditionalFieldValues } from "helpers/sanitizeConditionalFields";
import { Spinner } from "components/Spinner/Spinner";
import FormElements from "components/helpers/FormElements";
import { PanelButtons } from "components/helpers/panelHelpers/PanelButtons";
import { readOnlyFieldRenderer } from "components/helpers/ReadOnlyFieldRenderer";
import { FormBuilder, FieldRenderers, FormField } from "@avamae/formbuilder";

import _, { forEach } from "lodash";
import EntityPageLink from "components/EntityPageLink";
import {
  ACTION_KEY,
  ACTION_SELECTED_ID_KEY,
  APP_ROUTES,
  RELATED_FORM_DESTINATION_MODULE_NAME,
  endpoints,
} from "config";
import { useDispatch, useSelector } from "react-redux";
import { clearSubmitResponseErrors, selectSubmitResponse } from "reducers/submitResponse";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";
import {
  ActionTypes,
  AutoSelectAllValuesFields,
  ExtraPayloadItems,
  OriginFormData,
  RelatedFormExtraDataToDisplay,
  RelatedFormExtraDataToDisplayFields,
  RelatedFormIdsToDelete,
  RelatedFormSuccessResponseIds,
  resetRelatedFormReduxData,
  selectFormReadOnlyFields,
  selectOriginFormData,
  selectRelatedFormActionType,
  selectRelatedFormData,
  selectRelatedFormExtraPayloadItems,
  selectRelatedFormModuleName,
  selectRelatedFormOriginFormAutoSelectAllValuesFields,
  selectRelatedFormSuccessResponseIds,
  setFormReadOnlyFields,
  setRelatedFormExtraPayloadItems,
  setRelatedFormIdsToDelete,
} from "reducers/listPage";
import { useNavigate } from "react-router-dom";

import {
  selectCreatedDataParentFieldData,
  selectNestedFormModuleData,
  selectReloadNestedFormParentFormToggle,
  setReloadNestedFormParentFormToggle,
} from "reducers/nestedCrudMenu";
import { toCamelCase } from "helpers/formatFormFieldNames";

import { pascalCaseToCamelCase } from "helpers/pascalCaseToCamelCase";
import { CrudOptions } from "components/Category/CrudMenu";

import { selectRefreshAllFormData, setRefreshAllFormData } from "reducers/entity";

export type AxiosRequestConfigOrCallback<Data> =
  | AxiosRequestConfig
  | ((
      selectedRows: Data[],
      idFromRowOption?: React.ReactText | null,
      selectedIdIfMultipleIds?: number | string
    ) => AxiosRequestConfig);

/** Todo(Farouk): These types need to be exposed from formbuilder package */
export type Renderer<T> = (key: number | string, field: FormField<T>) => React.ReactNode;

export type FormFieldRendererOverride<T> = {
  [P in keyof T]?: Renderer<T>;
};

export type ConditionalFields = {
  [fieldName: string]: {
    dependentField: string;
    dependentValues: any[] | null;
    dependentType?: string; // e.g. typeof x === "string", you would put "string" as the value, and it will only check the value matches this type instead of an exact value
    useLabelInsteadOfId?: boolean; // Match based on label rather than id/value from datasource: {label: string; value: number}
    datasourceField?: string;
  };
};

interface readOnlyFields {
  fieldName: string;
  readOnlyRenderer: string;
}

export const parseSubmitResponseEndpointErrors = (errors: object) => {
  const parsedErrors = Object.entries(errors).map((item) => {
    let parsedTitle = `${item[0]}`;

    // " I D" at the end of single/multi select field names
    if (parsedTitle.endsWith(" I D")) {
      parsedTitle = parsedTitle.substring(0, parsedTitle.indexOf(" I D"));
    }

    // "B " at the start of boolean field names
    if (parsedTitle.startsWith("B ")) {
      parsedTitle = parsedTitle.substring(1, parsedTitle.length);
    }
    return {
      title: `${parsedTitle}`,
      text: `${item[1]}`,
    };
  });

  if (parsedErrors && Array.isArray(parsedErrors) && parsedErrors.length > 0) {
    return parsedErrors;
  } else {
    return null;
  }
};

export type OverrideParamId = {
  idParamOverrideName: string;
  idParamOverrideValue: any;
};

export type NestedFormDataGroupColumns = {
  view?: GroupColumns;
  create?: GroupColumns;
  edit?: GroupColumns;
};

export type CreatedDataSearchStringFieldNameData = {
  [key in APP_ROUTES]?: string;
};
export type NestedFormData = {
  selectedId?: number;
  actionType?: "create" | "edit" | "view";

  groupColumns?: NestedFormDataGroupColumns;
  parentFieldNamesToUpdate?: string[];
  createdDataSearchStringFieldName?: CreatedDataSearchStringFieldNameData;
  createdDataSearchIdFieldName?: CreatedDataSearchStringFieldNameData;
};

export const redirectOnDone = (
  createOrEdit: ActionTypes,
  destination: string,
  id: number | null
) => {
  const navigate = useNavigate();
  if (id !== null) {
    window.localStorage.setItem(ACTION_SELECTED_ID_KEY, `${id}`);
  }

  window.localStorage.setItem(ACTION_KEY, createOrEdit);

  navigate(destination);
};

type ExtraColumnComponents = {
  [column: number]: React.ReactComponentElement<any>;
};

export type ReadOnlyConditionalFields = {
  [fieldName: string]: {
    valuesToMakeOtherFieldsReadOnly: any[];
    equalAllOrSomeValues: "all" | "some";
    fieldsToMakeReadOnly: { fieldName: string; readOnlyRenderer: string }[];
  };
};

export type FormDatasourceData = { [fieldName: string]: any };

export const parseFormDatasourceData = (formDatasourceData: FormDatasourceData | null) => {
  const parsedFormDatasourceData: {
    [fieldName: string]: { [internalCode: string]: string };
  } = {}; // string is the label associated with the value from the datasource data
  if (formDatasourceData) {
    Object.keys(formDatasourceData).forEach((key) => {
      const value = formDatasourceData[key];
      let parsedValue: any = {};
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item.internalCode && typeof item.internalCode === "string" && item.value) {
            parsedValue[item.internalCode] = item.value;
          }
        });
      }

      parsedFormDatasourceData[key] = parsedValue;
    });
  }

  if (Object.keys(parsedFormDatasourceData).length > 0) {
    return parsedFormDatasourceData;
  } else {
    return null;
  }
};

export type GroupsWithBorders = {
  groupName: string;
  groupBorderColour: string;
}[];

export type ColumnsMaxWidths = {
  [columnNumber: number]: string;
};

type Props<Data, Fields extends object> = {
  validationSchema?: ObjectSchema<Fields> | any;
  initialDataUrl: string;
  fieldRenderers?: (data: any, refs?: any) => FieldRenderers<Fields>;
  overrideFormFieldRenderer?: FormFieldRendererOverride<Fields>;
  readOnlyFieldRenderers?: (data: any, refs?: any) => FieldRenderers<Fields>;
  submitUrl: string;
  onDone(): void;
  selectedData: Data[];
  formType: "json" | "multipart";
  readOnly?: boolean;
  idFromRowOption?: React.ReactText | null;
  additionalFieldsToSubmit?: [string, any][];
  fetchConfig?: AxiosRequestConfigOrCallback<Data>;
  nullStringValues: string[];
  removeReadonlyNullValues?: string[];
  submitButtonText?: string;
  wrapperClassName?: string;
  hideActionButton?: boolean;
  noSubmit?: boolean;
  idField?: string;
  selectedIds?: number[];
  conditionalFields?: ConditionalFields; // Show/hide certain fields based on another field's value. The supplied dependentValues are the values where we want to display the field based on the other field's value
  hiddenFields?: string[];
  hiddenGroups?: string[];
  groupColumns?: GroupColumns;
  entityPageLinkData?: {
    buttonText: string;
    destinationPage: APP_ROUTES;
    filterStringFieldName: string;
    idColumn: string;
  };
  hideAllGroupLabels?: boolean;
  hideSpecificGroupLabels?: string[]; // Group names
  hideGroupLabel?: boolean;
  hideGroupLabels?: string[];
  relatedFormDataCreateInfo?: {
    fieldName: string;

    code: string;
    valueFormFieldName?: string;
    isMulti?: boolean;
  }[];

  relatedFormExtraPayloadItems?: ExtraPayloadItems;
  relatedFormOriginFormAutoSelectAllValuesFields?: AutoSelectAllValuesFields;
  relatedFormExtraDataToDisplayFields?: RelatedFormExtraDataToDisplayFields;
  progressBar?: React.ReactNode;
  customClass?: string;
  validateAddress?: boolean;
  cancelOnDone?: () => void;
  saveSubmitResponse?: boolean;
  overrideFormId?: {
    idFieldOverrideName: string;
    idFieldOverrideValue: number | null;
  };
  overrideParamIds?: OverrideParamId[];
  extraPayloadItems?: { [paramName: string]: any };
  readOnlyFields?: readOnlyFields[];
  extraReadOnlyColumnComponents?: ExtraColumnComponents;
  alternateSubmitFunction?: () => void;
  onValuesChange?: (values: any) => void;
  autoMenuActionsCode?: CrudOptions;
  actionType?: "create" | "edit" | "view" | "update";
  nestedFormData?: NestedFormData;
  nestedFormDataComponent?: React.ReactNode; // Module component that calls <Category /> inside
  createdDataSearchStringFieldName?: CreatedDataSearchStringFieldNameData;
  createdDataSearchIdFieldName?: CreatedDataSearchStringFieldNameData;
  nestedFormModuleName?: APP_ROUTES;
  clickedIdIfMultiIds?: string | number | undefined;
  readOnlyConditionalFields?: ReadOnlyConditionalFields;
  multipleSelectedIds?: number[];
  fieldsInBoxes?: boolean;
  overrideFormReadOnlyFieldRenderer?: FormFieldRendererOverride<Fields>;
  formDatasourceData?: FormDatasourceData | null;
  setFormDatasourceData?: React.Dispatch<SetStateAction<FormDatasourceData | null>>;
  refs?: { selectRef?: any };
  overrideFormValue?: {
    idFieldOverrideName: string;
    idFieldOverrideValue: any;
  };
  hideViewGroupLabel?: boolean;
  groupSubColumns?: { [groupName: string]: number }; // The number is for the number of field grid columns within that overall column. e.g. {1: 3} means 3 grid columns within column 1.
  rightSectionFieldType?: string;
  groupsWithBorders?: GroupsWithBorders;
  columnsMaxWidths?: ColumnsMaxWidths;
  handleResponse?: (response: any) => void;
  hideSystemDetails?: boolean;
  alternatePanelButtons?: React.ReactElement;
  disableAutocomplete?: boolean;
  showUploadProgress?: boolean;
  uploadProgressText?: string;
  sanitizeConditionalFields?: boolean;
};

export type InitialDataResponse<Data> = {
  id: number;
  details: Data;
  metadata: FormField<Data>[];
  status: "0" | "1";
  errors: ErrorMessage[];
};

type ReadOnlyFormValuesToUpdate = {
  [fieldName: string]: { value: any; isMulti?: boolean };
};

export type RelatedFormUpdatedIdFieldValues = { [fieldName: string]: any };

function Entity<Data, Fields extends object>(props: Props<Data, Fields>): JSX.Element {
  const {
    selectedData,
    onDone,
    idFromRowOption,
    initialDataUrl,
    submitUrl,
    formType,
    validationSchema,
    additionalFieldsToSubmit,
    nullStringValues,
    removeReadonlyNullValues,
    readOnly,
    fieldRenderers,
    overrideFormFieldRenderer,
    readOnlyFieldRenderers,
    submitButtonText,
    fetchConfig,
    wrapperClassName = "",
    hideActionButton = false,
    noSubmit = false,
    conditionalFields,
    hiddenFields,
    hiddenGroups,
    groupColumns,
    entityPageLinkData,
    selectedIds,
    idField,
    customClass,
    progressBar,
    hideAllGroupLabels = false,
    hideSpecificGroupLabels,
    hideGroupLabel = false,
    hideGroupLabels,
    relatedFormDataCreateInfo,

    relatedFormExtraPayloadItems,
    relatedFormOriginFormAutoSelectAllValuesFields,
    relatedFormExtraDataToDisplayFields,
    cancelOnDone,
    saveSubmitResponse,
    overrideFormId,
    overrideParamIds,
    extraPayloadItems,
    readOnlyFields,
    extraReadOnlyColumnComponents,
    alternateSubmitFunction,
    onValuesChange,
    autoMenuActionsCode,
    actionType,
    nestedFormData,
    nestedFormDataComponent,
    createdDataSearchStringFieldName,
    createdDataSearchIdFieldName,
    nestedFormModuleName,
    clickedIdIfMultiIds,
    readOnlyConditionalFields,
    overrideFormReadOnlyFieldRenderer,
    formDatasourceData,
    setFormDatasourceData,
    refs,
    overrideFormValue,
    hideViewGroupLabel,
    groupSubColumns,
    rightSectionFieldType,
    groupsWithBorders,
    columnsMaxWidths,
    multipleSelectedIds,
    handleResponse,
    fieldsInBoxes,
    hideSystemDetails = false,
    alternatePanelButtons,
    disableAutocomplete = false,
    showUploadProgress = false,
    uploadProgressText,
    sanitizeConditionalFields = false,
  } = props;

  const [idsToDeleteFromRelatedForm, setIdsToDeleteFromRelatedForm] =
    useState<RelatedFormIdsToDelete | null>(null);

  const [metadataDatasourceFields, setMetadataDatasourceFields] = useState<string[] | null>(null);

  useEffect(() => {
    dispatch(setRelatedFormIdsToDelete(idsToDeleteFromRelatedForm));
  }, [idsToDeleteFromRelatedForm]);

  const [redirectOnDoneData, setRedirectOnDoneData] = useState<{
    createOrEdit: ActionTypes;
    destination: string;
    id: number | null;
  } | null>(null);

  const cfg = React.useMemo(() => {
    return fetchConfig
      ? getAxiosRequestConfig(fetchConfig, selectedData, idFromRowOption, clickedIdIfMultiIds)
      : undefined;
  }, [fetchConfig, selectedData, idFromRowOption, clickedIdIfMultiIds]);

  const { data, loading, error } = useFetch<InitialDataResponse<Fields>>(initialDataUrl, cfg);
  const formContainerRef = useRef<HTMLInputElement | null>(null);

  const formikRef = useRef<FormikProps<any>>(null) as any;

  const [parsedSubmitResponseErrors, setParsedSubmitResponseErrors] = useState<any | null>(null);

  useEffect(() => {
    if (formContainerRef && formContainerRef.current && formContainerRef.current.scrollTo) {
      formContainerRef.current.scrollTo(0, 0);
    }
  }, [loading]);

  const dispatch = useDispatch();

  const submitResponseErrors = useSelector(selectSubmitResponse).submitResponseErrors;

  const hasMounted = useRef<boolean>(false);

  useEffect(() => {
    if (
      submitResponseErrors &&
      Object.keys(submitResponseErrors).length > 0 &&
      parseSubmitResponseEndpointErrors(submitResponseErrors) &&
      hasMounted.current
    ) {
      setParsedSubmitResponseErrors(parseSubmitResponseEndpointErrors(submitResponseErrors));

      dispatch(clearSubmitResponseErrors());
    }
    if (!hasMounted.current) {
      dispatch(clearSubmitResponseErrors());
      hasMounted.current = true;
    }
  }, [submitResponseErrors]);

  useEffect(() => {
    if (parsedSubmitResponseErrors) {
      toast(<ToastMessage toastData={parsedSubmitResponseErrors} className="ToastError" />, {
        ...toastConfig,
      });
    }
  }, [parsedSubmitResponseErrors]);

  const allRelatedFormExtraPayloadItems = useSelector(selectRelatedFormExtraPayloadItems);

  const originFormData = useSelector(selectOriginFormData);

  const nestedFormModuleData = useSelector(selectNestedFormModuleData);

  const existingCreatedDataParentFieldData = useSelector(selectCreatedDataParentFieldData);

  let handleSubmit = createSubmitForm(
    submitUrl,

    redirectOnDoneData
      ? () =>
          redirectOnDone(
            redirectOnDoneData.createOrEdit,
            redirectOnDoneData.destination,
            redirectOnDoneData.id
          )
      : onDone,
    dispatch,
    allRelatedFormExtraPayloadItems ?? undefined,
    relatedFormOriginFormAutoSelectAllValuesFields ?? undefined,
    originFormData ?? undefined,
    relatedFormExtraDataToDisplayFields ?? undefined,
    saveSubmitResponse,
    extraPayloadItems ?? undefined,
    nestedFormData ?? undefined,
    nestedFormModuleData ?? undefined,
    createdDataSearchStringFieldName ?? undefined,
    createdDataSearchIdFieldName ?? undefined,
    nestedFormModuleName ?? undefined,
    existingCreatedDataParentFieldData ?? undefined,
    metadataDatasourceFields ?? undefined,
    handleResponse ?? undefined
  );

  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const handleOnDone = () => {
    setUploadProgress(0);
    if (redirectOnDoneData) {
      redirectOnDone(
        redirectOnDoneData.createOrEdit,
        redirectOnDoneData.destination,
        redirectOnDoneData.id
      );
    } else {
      onDone();
    }
  };

  if (formType === "multipart")
    handleSubmit = createMultipartSubmitForm(
      submitUrl,
      handleOnDone,
      dispatch,
      additionalFieldsToSubmit ?? [],
      allRelatedFormExtraPayloadItems ?? undefined,
      relatedFormOriginFormAutoSelectAllValuesFields ?? undefined,
      originFormData ?? undefined,
      relatedFormExtraDataToDisplayFields ?? undefined,
      saveSubmitResponse,
      extraPayloadItems ?? undefined,
      nestedFormData ?? undefined,
      nestedFormModuleData ?? undefined,
      createdDataSearchStringFieldName ?? undefined,
      createdDataSearchIdFieldName ?? undefined,
      nestedFormModuleName ?? undefined,
      existingCreatedDataParentFieldData ?? undefined,
      metadataDatasourceFields ?? undefined,
      setUploadProgress
    );

  const formReadOnlyFields = useSelector(selectFormReadOnlyFields);

  useEffect(() => {
    if (relatedFormDataCreateInfo) {
      let readOnlyFieldNames: { [key: string]: string } = {};

      relatedFormDataCreateInfo.forEach((item) => {
        readOnlyFieldNames[item.fieldName] = item.code;
      });

      if (
        readOnlyFieldNames &&
        Object.keys(readOnlyFieldNames).length === 0 &&
        formReadOnlyFields !== null
      ) {
        dispatch(setFormReadOnlyFields(null));
      } else if (formReadOnlyFields !== readOnlyFieldNames) {
        dispatch(setFormReadOnlyFields(readOnlyFieldNames));
      }
    } else if (formReadOnlyFields !== null) {
      dispatch(setFormReadOnlyFields(null));
    }
  }, []);

  useEffect(() => {
    if (
      !loading &&
      overrideFormId &&
      overrideFormId.idFieldOverrideName &&
      overrideFormId.idFieldOverrideValue &&
      formikRef &&
      formikRef.current &&
      formikRef.current.values &&
      formikRef.current.values.hasOwnProperty(overrideFormId.idFieldOverrideName) &&
      formikRef.current.values[overrideFormId.idFieldOverrideName] !==
        overrideFormId.idFieldOverrideValue &&
      formikRef.current.setFieldValue
    ) {
      formikRef.current.setFieldValue(
        overrideFormId.idFieldOverrideName,
        overrideFormId.idFieldOverrideValue
      );
    }
  }, [loading, overrideFormId, formikRef]);

  useEffect(() => {
    if (
      !loading &&
      overrideFormValue &&
      overrideFormValue.idFieldOverrideName &&
      overrideFormValue.idFieldOverrideValue &&
      formikRef &&
      formikRef.current &&
      formikRef.current.values &&
      formikRef.current.values.hasOwnProperty(overrideFormValue.idFieldOverrideName) &&
      formikRef.current.values[overrideFormValue.idFieldOverrideName] !==
        overrideFormValue.idFieldOverrideValue &&
      formikRef.current.setFieldValue
    ) {
      formikRef.current.setFieldValue(
        overrideFormValue.idFieldOverrideName,
        overrideFormValue.idFieldOverrideValue
      );
    }
  }, [loading, overrideFormValue, formikRef]);

  useEffect(() => {
    // This will clash if you are trying to use both overrideFormId and overrideParamIds, so make sure you are only using 1 at a time

    if (
      !loading &&
      !overrideFormId &&
      overrideParamIds &&
      formikRef.current &&
      formikRef.current.values &&
      formikRef.current.setValues
    ) {
      let newValues = _.cloneDeep(formikRef.current.values);

      overrideParamIds.forEach((item) => {
        newValues[item.idParamOverrideName] = item.idParamOverrideValue;
      });

      formikRef.current.setValues(newValues);
    }
  }, [loading, overrideParamIds, overrideFormId, formikRef]);

  const reloadNestedFormParentFormToggle = useSelector(selectReloadNestedFormParentFormToggle);

  const updateFormData = async (
    currentFormData: any,
    currentMetadata: FormField<Fields>[] | undefined,
    initialDataUrl: string,
    cfg: AxiosRequestConfig<any> | undefined
  ) => {
    let fieldsToUpdate: string[] = [];

    if (currentMetadata) {
      currentMetadata.forEach((item) => {
        if (item.dataSource) {
          fieldsToUpdate.push(pascalCaseToCamelCase(`${item.dataSource}`).trim());
        }
      });
    }

    try {
      const response = await api.get(initialDataUrl, cfg);

      if (response.status === 200 && response.data.status === `1`) {
        if (response.data.details && typeof response.data.details === "object") {
          if (
            fieldsToUpdate &&
            fieldsToUpdate.length > 0 &&
            formikRef &&
            formikRef.current &&
            formikRef.current.setFieldValue
          ) {
            fieldsToUpdate.forEach((fieldName) => {
              if (
                response.data.details.hasOwnProperty(fieldName) &&
                currentFormData.hasOwnProperty(fieldName)
              ) {
                formikRef.current.setFieldValue(fieldName, response.data.details[fieldName]);
              }
            });
          }
        }
      }
    } catch (error) {
      console.log("failed to update form data: ", error);
    }
  };

  useEffect(() => {
    if (reloadNestedFormParentFormToggle && formikRef.current) {
      const currentFormData = formikRef.current.values;

      dispatch(setReloadNestedFormParentFormToggle(false));

      if (currentFormData && initialDataUrl) {
        updateFormData(currentFormData, data?.metadata, initialDataUrl, cfg);
      }
    }
  }, [reloadNestedFormParentFormToggle]);

  const bRefreshAllFormData = useSelector(selectRefreshAllFormData);

  const refreshAllFormData = async (
    currentFormData: any,
    currentMetadata: FormField<Fields>[] | undefined,
    initialDataUrl: string,
    cfg: AxiosRequestConfig<any> | undefined
  ) => {
    let fieldsToUpdate: string[] = [];

    if (currentMetadata) {
      currentMetadata.forEach((item) => {
        fieldsToUpdate.push(pascalCaseToCamelCase(`${String(item.name)}`).trim());
      });
    }

    try {
      const response = await api.get(initialDataUrl, cfg);

      if (response.status === 200 && response.data.status === `1`) {
        if (response.data.details && typeof response.data.details === "object") {
          if (
            fieldsToUpdate &&
            fieldsToUpdate.length > 0 &&
            formikRef &&
            formikRef.current &&
            formikRef.current.setFieldValue
          ) {
            fieldsToUpdate.forEach((fieldName) => {
              if (
                response.data.details.hasOwnProperty(fieldName) &&
                currentFormData.hasOwnProperty(fieldName)
              ) {
                formikRef.current.setFieldValue(fieldName, response.data.details[fieldName]);
              }
            });
          }
        }
      }
    } catch (error) {
      console.log("failed to update form data: ", error);
    }
  };

  useEffect(() => {
    if (bRefreshAllFormData && formikRef.current) {
      const currentFormData = formikRef.current.values;

      dispatch(setRefreshAllFormData(false));

      if (currentFormData && initialDataUrl) {
        refreshAllFormData(currentFormData, data?.metadata, initialDataUrl, cfg);
      }
    }
  }, [bRefreshAllFormData]);

  useEffect(() => {
    if (data && data.metadata && Array.isArray(data.metadata)) {
      const datasourceFields = data.metadata
        .filter(
          (item) =>
            item.dataSource &&
            ((typeof item.type === "string" &&
              item.type.toLowerCase().startsWith("singleselect")) ||
              (typeof item.type === "string" && item.type.toLowerCase().startsWith("multiselect")))
        )
        .map((item) => pascalCaseToCamelCase(item.dataSource ?? ""))
        .filter((item) => item !== "");

      if (metadataDatasourceFields !== datasourceFields) {
        setMetadataDatasourceFields(datasourceFields);
      }
    } else if (metadataDatasourceFields !== null) {
      setMetadataDatasourceFields(null);
    }
  }, [data]);

  useEffect(() => {
    if (
      setFormDatasourceData &&
      metadataDatasourceFields &&
      metadataDatasourceFields.length > 0 &&
      data &&
      data?.details
    ) {
      let metadataDatasourceData: any = {};

      metadataDatasourceFields.forEach((item) => {
        if (data.details.hasOwnProperty(item)) {
          metadataDatasourceData[item] = data.details[item as keyof typeof data.details];
        }
      });

      if (metadataDatasourceData !== formDatasourceData) {
        setFormDatasourceData(metadataDatasourceData);
      }
    } else if (setFormDatasourceData && formDatasourceData !== null) {
      setFormDatasourceData(null);
    }
  }, [metadataDatasourceFields]);

  if (loading && !data) {
    return <Spinner positionAbsolute />;
  }

  if (error) {
    throw error;
  }

  if (data) {
    const systemDetails = data.metadata?.filter((d) =>
      SYSTEM_DETAILS_KEYS.includes((d.name as string).toLowerCase())
    );
    let filteredMetaData = data.metadata?.filter(
      (d) => !SYSTEM_DETAILS_KEYS.includes((d.name as string).toLowerCase())
    );

    const systemDetailsData = {
      ...data,
      metadata: systemDetails,
    };
    // Read only fields:
    let tempFilterMetaData = _.cloneDeep(filteredMetaData);

    if (readOnlyFields) {
      readOnlyFields.forEach((field) => {
        tempFilterMetaData.forEach((meta, _i) => {
          if (meta.name === field.fieldName) {
            tempFilterMetaData[_i] = {
              ...meta,
              type: field.readOnlyRenderer,
            };
          }
        });
      });
    }
    //Conidtional readonly null values
    if (removeReadonlyNullValues) {
      removeReadonlyNullValues.forEach((field) => {
        let meta = tempFilterMetaData.find((meta, _i) => meta.name === field);
        if (meta) {
          const camelCaseFieldName = toCamelCase(field);
          if (
            data.details &&
            typeof data.details === "object" &&
            data.details.hasOwnProperty(camelCaseFieldName) &&
            data.details[camelCaseFieldName as keyof typeof data.details] == null
          ) {
            tempFilterMetaData.splice(tempFilterMetaData.indexOf(meta), 1);
          }
        }
      });
    }

    // Conditional Read only fields:
    if (readOnlyConditionalFields) {
      Object.keys(readOnlyConditionalFields).forEach((fieldName) => {
        const conditionalData = readOnlyConditionalFields[fieldName];
        if (
          data.details &&
          typeof data.details === "object" &&
          data.details.hasOwnProperty(fieldName)
        ) {
          const formValues = data.details[fieldName as keyof typeof data.details];

          let similarities = 0;
          let formValuesLength = 1;

          if (Array.isArray(formValues)) {
            const fieldValues = formValues as any[];
            formValuesLength = fieldValues.length;

            let arraySimilarities = fieldValues.filter(
              (o1) => !conditionalData.valuesToMakeOtherFieldsReadOnly.some((o2) => o1.id === o2.id)
            );

            similarities = arraySimilarities.length;
          } else {
            if (conditionalData.valuesToMakeOtherFieldsReadOnly.includes(formValues)) {
              similarities = 1;
            }
          }

          let makeFieldsReadOnly = false;
          if (conditionalData.equalAllOrSomeValues === "all") {
            if (similarities === formValuesLength) {
              makeFieldsReadOnly = true;
            }
          } else if (conditionalData.equalAllOrSomeValues === "some") {
            if (similarities > 0) {
              makeFieldsReadOnly = true;
            }
          }

          if (makeFieldsReadOnly) {
            conditionalData.fieldsToMakeReadOnly.forEach((conditionalField) => {
              if (data.details.hasOwnProperty(conditionalField.fieldName)) {
                tempFilterMetaData.forEach((meta, _i) => {
                  if (meta.name === conditionalField.fieldName) {
                    tempFilterMetaData[_i] = {
                      ...meta,
                      type: conditionalField.readOnlyRenderer,
                    };
                  }
                });
              }
            });
          }
        }
      });
    }

    let rightSectionFieldTypeGroupedMetaData: _.Dictionary<FormField<Fields>[]> | null = null;
    if (rightSectionFieldType) {
      const rightSectionFieldTypeData = tempFilterMetaData.filter(
        (item) => item.type === rightSectionFieldType
      );

      rightSectionFieldTypeGroupedMetaData = _.groupBy(rightSectionFieldTypeData, "groupLabel");

      // Remove field with rightSectionFieldType type as we will display it in the right section on its own
      tempFilterMetaData = tempFilterMetaData.filter((item) => item.type !== rightSectionFieldType);
    }

    filteredMetaData = _.cloneDeep(tempFilterMetaData);

    const groupedMetaData = _.groupBy(filteredMetaData, "groupLabel");

    const readOnlyRenderers = {
      ...readOnlyFieldRenderer,

      ...(readOnlyFieldRenderers ? readOnlyFieldRenderers(data, refs) : {}),
    };

    const numberOfColumns =
      groupColumns && Object.values(groupColumns).includes(3)
        ? 3
        : groupColumns && Object.values(groupColumns).includes(2)
          ? 2
          : 1;

    const column1MaxWidth =
      columnsMaxWidths && columnsMaxWidths.hasOwnProperty(1) ? columnsMaxWidths[1] : null;

    const column2MaxWidth =
      columnsMaxWidths && columnsMaxWidths.hasOwnProperty(2) ? columnsMaxWidths[2] : null;

    return (
      <Formik
        initialValues={
          multipleSelectedIds
            ? {
                ...cleanupNullFields(data.details, nullStringValues),
                ids: multipleSelectedIds,
              }
            : selectedIds && idField
              ? {
                  ...cleanupNullFields(data.details, nullStringValues),
                  [idField]: selectedIds,
                }
              : cleanupNullFields(data.details, nullStringValues)
        }
        validationSchema={validationSchema}
        onSubmit={
          sanitizeConditionalFields && conditionalFields
            ? (values, formikHelpers) =>
                handleSubmit(
                  sanitizeConditionalFieldValues(
                    values,
                    conditionalFields,
                    formDatasourceData ?? undefined
                  ),
                  formikHelpers
                )
            : handleSubmit
        }
        innerRef={formikRef}
      >
        {(formik) => {
          useEffect(() => {
            if (onValuesChange && formik && formik.values) {
              onValuesChange(formik.values);
            }
          }, [formik.values]);
          return (
            <div className="FormWrapper">
              {showUploadProgress && uploadProgress > 0 && (
                <div className="UploadPercentageOverlay">
                  <div className="UploadProgressBG">
                    <div
                      className="UploadProgressBar"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                    <div className="UploadProgressText">
                      {uploadProgressText ? uploadProgressText : `Submitting: `} {uploadProgress}%
                    </div>
                  </div>
                </div>
              )}

              <Form
                data-testid="entity-form"
                className={wrapperClassName}
                autoComplete={disableAutocomplete ? "off" : "on"}
              >
                <div className={`Content ${actionType ? `${actionType}` : ""}`}>
                  <div
                    className={`Form ${
                      progressBar ? "ProgressBarVisible" : ""
                    } ${fieldsInBoxes ? "FieldsInBoxes" : ""}`}
                    ref={formContainerRef}
                  >
                    {readOnly ? (
                      <div
                        className={`ReadOnlyContainer ${
                          numberOfColumns === 1 ? "OneColumn" : "TwoColumns"
                        }
                      ${
                        rightSectionFieldType && rightSectionFieldTypeGroupedMetaData
                          ? "RightSection"
                          : ""
                      }
                      `}
                      >
                        <div className={`Column1 Column `}>
                          <RenderViewGroup
                            groupedMetaData={groupedMetaData}
                            data={data}
                            readOnlyRenderers={readOnlyRenderers}
                            groupColumns={groupColumns}
                            columnNumber={1}
                            groupSubColumns={groupSubColumns}
                            formik={formik}
                            conditionalFields={conditionalFields}
                            hiddenFields={hiddenFields}
                            hiddenGroups={hiddenGroups}
                            hideGroupLabels={hideGroupLabels}
                            hideGroupLabel={hideGroupLabel}
                            overrideFormReadOnlyFieldRenderer={overrideFormReadOnlyFieldRenderer}
                            hideViewGroupLabel={hideViewGroupLabel}
                            groupsWithBorders={groupsWithBorders}
                            hideSpecificGroupLabels={hideSpecificGroupLabels}
                          />

                          {systemDetailsData.metadata.length > 0 && !hideSystemDetails && (
                            <div className="Group SystemDetails">
                              <h4 className="SubTitle SystemDetails">System Details</h4>

                              <FormBuilder
                                data={systemDetailsData}
                                loadingFallback={<Spinner positionAbsolute />}
                                readOnly
                                fieldRenderers={readOnlyRenderers}
                                fieldProps={{
                                  addDate: {
                                    dateFormat: "dd-MMM-yyyy",
                                  },
                                  editDate: {
                                    dateFormat: "dd-MMM-yyyy",
                                  },
                                }}
                              />
                            </div>
                          )}

                          {extraReadOnlyColumnComponents &&
                            extraReadOnlyColumnComponents[1] &&
                            extraReadOnlyColumnComponents[1]}
                        </div>

                        <div className={`Column2 Column `}>
                          <RenderViewGroup
                            groupedMetaData={groupedMetaData}
                            data={data}
                            readOnlyRenderers={readOnlyRenderers}
                            groupColumns={groupColumns}
                            columnNumber={2}
                            groupSubColumns={groupSubColumns}
                            formik={formik}
                            conditionalFields={conditionalFields}
                            hiddenFields={hiddenFields}
                            overrideFormReadOnlyFieldRenderer={overrideFormReadOnlyFieldRenderer}
                            hiddenGroups={hiddenGroups}
                            hideViewGroupLabel={hideViewGroupLabel}
                            hideGroupLabels={hideGroupLabels}
                            hideGroupLabel={hideGroupLabel}
                            groupsWithBorders={groupsWithBorders}
                            hideSpecificGroupLabels={hideSpecificGroupLabels}
                          />
                          {extraReadOnlyColumnComponents &&
                            extraReadOnlyColumnComponents[2] &&
                            extraReadOnlyColumnComponents[2]}

                          {extraReadOnlyColumnComponents &&
                            extraReadOnlyColumnComponents[3] &&
                            extraReadOnlyColumnComponents[3]}
                        </div>
                      </div>
                    ) : (
                      <div
                        className={`CreateEditContainer ${
                          numberOfColumns === 1 ? "OneColumn" : "TwoColumns"
                        } ${actionType ? `${actionType}` : ""} ${
                          rightSectionFieldType && rightSectionFieldTypeGroupedMetaData
                            ? "RightSection"
                            : ""
                        }`}
                      >
                        <div className={`Column1 Column ${customClass}`}>
                          <RenderCreateUpdateGroup
                            groupedMetaData={groupedMetaData}
                            conditionalFields={conditionalFields}
                            hiddenFields={hiddenFields}
                            data={data}
                            fieldRenderers={fieldRenderers}
                            overrideFormFieldRenderer={overrideFormFieldRenderer}
                            formik={formik}
                            groupColumns={groupColumns}
                            columnNumber={1}
                            groupSubColumns={groupSubColumns}
                            hideAllGroupLabels={hideAllGroupLabels}
                            hideGroupLabels={hideGroupLabels}
                            hideGroupLabel={hideGroupLabel}
                            hideSpecificGroupLabels={hideSpecificGroupLabels}
                            hiddenGroups={hiddenGroups}
                            refs={{
                              selectRef: refs?.selectRef,
                              containerRef: formContainerRef,
                            }}
                            groupsWithBorders={groupsWithBorders}
                          />
                        </div>

                        <div className={`Column2 Column `}>
                          <RenderCreateUpdateGroup
                            groupedMetaData={groupedMetaData}
                            conditionalFields={conditionalFields}
                            hiddenFields={hiddenFields}
                            data={data}
                            fieldRenderers={fieldRenderers}
                            overrideFormFieldRenderer={overrideFormFieldRenderer}
                            formik={formik}
                            groupColumns={groupColumns}
                            columnNumber={2}
                            groupSubColumns={groupSubColumns}
                            hideAllGroupLabels={hideAllGroupLabels}
                            hideSpecificGroupLabels={hideSpecificGroupLabels}
                            hideGroupLabels={hideGroupLabels}
                            hideGroupLabel={hideGroupLabel}
                            hiddenGroups={hiddenGroups}
                            refs={{
                              selectRef: refs?.selectRef,
                              containerRef: formContainerRef,
                            }}
                            groupsWithBorders={groupsWithBorders}
                          />
                        </div>
                      </div>
                    )}

                    {entityPageLinkData && (
                      <EntityPageLink
                        buttonText={entityPageLinkData.buttonText}
                        destinationPage={entityPageLinkData.destinationPage}
                        filterStringFieldName={entityPageLinkData.filterStringFieldName}
                        selectedId={
                          selectedData &&
                          selectedData.length === 1 &&
                          selectedData[0] &&
                          typeof selectedData[0] === "object" &&
                          selectedData[0].hasOwnProperty(entityPageLinkData.idColumn)
                            ? `${
                                selectedData[0][
                                  entityPageLinkData.idColumn as keyof (typeof selectedData)[0]
                                ]
                              }`
                            : undefined
                        }
                      />
                    )}
                  </div>

                  {progressBar && progressBar}

                  {!alternatePanelButtons ? (
                    <PanelButtons
                      close={() => {
                        // Cancel button right panel:
                        if (
                          autoMenuActionsCode &&
                          (!actionType ||
                            actionType.toLowerCase() === autoMenuActionsCode?.toLowerCase())
                        ) {
                          onDone();
                          return;
                        }

                        if (cancelOnDone) {
                          cancelOnDone();
                        } else {
                          onDone();
                        }
                      }}
                      submitButtonText={submitButtonText ?? "Submit"}
                      hideActionButton={hideActionButton ? true : false}
                      noSubmit={noSubmit}
                      alternateSubmitFunction={alternateSubmitFunction}
                      extraStyles={
                        rightSectionFieldType && rightSectionFieldTypeGroupedMetaData
                          ? {
                              position: "fixed",
                              width: "100%",
                              minWidth: "100%",
                              maxWidth: "100%",
                              boxSizing: "border-box",
                              zIndex: "1",
                            }
                          : undefined
                      }
                    />
                  ) : (
                    alternatePanelButtons
                  )}

                  {nestedFormDataComponent && (
                    <div className="NestedFormDataComponent">{nestedFormDataComponent}</div>
                  )}
                </div>
              </Form>

              {rightSectionFieldType && rightSectionFieldTypeGroupedMetaData && readOnly && (
                <div
                  className={`RightSectionContainer ReadOnly ${
                    numberOfColumns === 1 ? "OneColumn" : "TwoColumns"
                  }`}
                  style={
                    column1MaxWidth
                      ? column1MaxWidth !== "100%"
                        ? {
                            maxWidth: `calc(100% - ${column1MaxWidth})`,
                            minWidth: `calc(100% - ${column1MaxWidth})`,
                          }
                        : {
                            maxWidth: `100%`,
                            minWidth: `100%`,
                          }
                      : {}
                  }
                >
                  <RenderViewGroup
                    groupedMetaData={rightSectionFieldTypeGroupedMetaData}
                    data={data}
                    readOnlyRenderers={readOnlyRenderers}
                    groupColumns={groupColumns}
                    columnNumber={1}
                    groupSubColumns={groupSubColumns}
                    formik={formik}
                    conditionalFields={conditionalFields}
                    hiddenFields={hiddenFields}
                    hiddenGroups={hiddenGroups}
                    overrideFormReadOnlyFieldRenderer={overrideFormReadOnlyFieldRenderer}
                    hideViewGroupLabel={hideViewGroupLabel}
                    hideGroupLabels={hideGroupLabels}
                    hideGroupLabel={hideGroupLabel}
                    groupsWithBorders={groupsWithBorders}
                    hideSpecificGroupLabels={hideSpecificGroupLabels}
                  />
                </div>
              )}

              {rightSectionFieldType && rightSectionFieldTypeGroupedMetaData && !readOnly && (
                <div
                  className={`RightSectionContainer ${
                    numberOfColumns === 1 ? "OneColumn" : "TwoColumns"
                  }`}
                  style={
                    column1MaxWidth
                      ? column1MaxWidth !== "100%"
                        ? {
                            maxWidth: `calc(100% - ${column1MaxWidth})`,
                            minWidth: `calc(100% - ${column1MaxWidth})`,
                          }
                        : {
                            maxWidth: `100%`,
                            minWidth: `100%`,
                          }
                      : {}
                  }
                >
                  <RenderCreateUpdateGroup
                    groupedMetaData={rightSectionFieldTypeGroupedMetaData}
                    conditionalFields={conditionalFields}
                    hiddenFields={hiddenFields}
                    data={data}
                    fieldRenderers={fieldRenderers}
                    overrideFormFieldRenderer={overrideFormFieldRenderer}
                    formik={formik}
                    groupColumns={groupColumns}
                    columnNumber={1}
                    groupSubColumns={groupSubColumns}
                    hideAllGroupLabels={hideAllGroupLabels}
                    hideSpecificGroupLabels={hideSpecificGroupLabels}
                    hideGroupLabels={hideGroupLabels}
                    hideGroupLabel={hideGroupLabel}
                    hiddenGroups={hiddenGroups}
                    refs={{
                      selectRef: refs?.selectRef,
                      containerRef: formContainerRef,
                    }}
                  />
                </div>
              )}
            </div>
          );
        }}
      </Formik>
    );
  }

  return <div className={styles.container}></div>;
}

function cleanupNullFields<Fields extends object>(fields: Fields, fieldsToClean: string[]) {
  const cleanedUpFields: any = { ...fields };

  for (const key of fieldsToClean) {
    if (cleanedUpFields[key] == null) {
      cleanedUpFields[key] = "";
    }
  }

  return cleanedUpFields;
}

function isConfig<Data>(cfg: AxiosRequestConfigOrCallback<Data>): cfg is AxiosRequestConfig {
  if ((cfg as Function).call != null) {
    return false;
  }

  return true;
}

export function getAxiosRequestConfig<Data>(
  cfg: AxiosRequestConfigOrCallback<Data>,
  data: Data[],
  idFromRowOption?: React.ReactText | null,
  clickedIdIfMultiIds?: string | number
): AxiosRequestConfig {
  if (isConfig(cfg)) return cfg;

  return cfg(data, idFromRowOption, clickedIdIfMultiIds);
}
export default Entity;

const filterGroupedMetaData = (
  groupedMetaData: any,
  formik: FormikProps<any>,
  key: string,
  conditionalFields: ConditionalFields | undefined,
  hiddenFields: string[] | undefined
) => {
  let filteredGroupedMetaData: any[] = [];

  let fieldsToHide: { [key: string]: boolean | undefined } = {};
  if (conditionalFields) {
    groupedMetaData[key].forEach((item: any) => {
      if (!conditionalFields.hasOwnProperty(item.name)) {
        filteredGroupedMetaData = [...filteredGroupedMetaData, item];
      } else {
        const dependentData = conditionalFields[item.name as keyof typeof conditionalFields];
        let dependentFieldName = dependentData.dependentField;

        dependentFieldName = `${
          dependentFieldName.charAt(0).toLowerCase() + dependentFieldName.slice(1)
        }`.trim();

        const fieldValue = formik.values[dependentFieldName as keyof typeof formik.values];

        let fieldValueCode: any = null;

        if (
          dependentData.datasourceField &&
          formik.values.hasOwnProperty(dependentData.datasourceField)
        ) {
          const datasource = formik.values[dependentData.datasourceField];

          if (
            Array.isArray(datasource) &&
            datasource.length > 0 &&
            typeof datasource[0] === "object" &&
            datasource[0].hasOwnProperty("internalCode") &&
            datasource[0].hasOwnProperty("value") &&
            typeof fieldValue === "number"
          ) {
            const match = datasource.filter((item) => item.value === fieldValue);

            if (
              Array.isArray(match) &&
              match.length === 1 &&
              match[0].hasOwnProperty("internalCode") &&
              typeof match[0].internalCode === "string"
            ) {
              fieldValueCode = match[0].internalCode;
            }
          }
        }

        const dependentValues = dependentData.dependentValues;

        let showField =
          dependentData.datasourceField &&
          fieldValueCode &&
          dependentValues &&
          dependentValues.includes(fieldValueCode)
            ? true
            : dependentValues && dependentValues.includes(fieldValue)
              ? true
              : false;

        if (dependentData.dependentType && typeof fieldValue === dependentData.dependentType) {
          showField = true;
        } else if (
          dependentData.dependentType &&
          Array.isArray(fieldValue) &&
          fieldValue.length > 0 &&
          typeof fieldValue[0] === dependentData.dependentType
        ) {
          showField = true;
        }

        if (showField) {
          filteredGroupedMetaData = [...filteredGroupedMetaData, item];
        } else {
          const parsedName = `${item.name.charAt(0).toLowerCase() + item.name.slice(1)}`.trim();
          fieldsToHide[parsedName] = true;
        }
      }
    });
  } else {
    filteredGroupedMetaData = groupedMetaData[key];
  }

  if (hiddenFields) {
    hiddenFields.forEach((item) => {
      if (!fieldsToHide.hasOwnProperty(item)) {
        fieldsToHide[item] = true;
      }
    });
  }

  return [filteredGroupedMetaData, fieldsToHide];
};

export type GroupColumns = { [fieldName: string]: number };

type RenderViewGroupProps<Data, Fields extends object> = {
  groupedMetaData: _.Dictionary<FormField<any>[]>;
  conditionalFields: ConditionalFields | undefined;

  data: InitialDataResponse<any>;
  formik: FormikProps<any>;
  hiddenFields: string[] | undefined;
  hiddenGroups: string[] | undefined;

  readOnlyRenderers: FieldRenderers<any>;

  groupColumns: GroupColumns | undefined;
  groupSubColumns?: { [groupName: string]: number };
  columnNumber: number;
  hideAllGroupLabels?: boolean;
  hideSpecificGroupLabels?: string[];
  hideGroupLabel?: boolean;
  hideGroupLabels?: string[];
  hideViewGroupLabel?: boolean;
  overrideFormReadOnlyFieldRenderer?: FormFieldRendererOverride<Fields>;
  groupsWithBorders?: GroupsWithBorders;
};

function RenderViewGroup<Data, Fields extends object>({
  groupedMetaData,
  data,
  readOnlyRenderers,
  groupColumns,
  groupSubColumns,
  columnNumber,
  hideAllGroupLabels = false,
  hideSpecificGroupLabels,
  hideGroupLabel = false,
  hideGroupLabels,
  hideViewGroupLabel = false,
  formik,
  hiddenFields,
  hiddenGroups,
  conditionalFields,
  overrideFormReadOnlyFieldRenderer,
  groupsWithBorders,
}: RenderViewGroupProps<Data, Fields>): JSX.Element {
  return (
    <Fragment>
      {Object.keys(groupedMetaData).map((key, _i) => {
        let [filteredGroupedMetaData, fieldsToHide] = filterGroupedMetaData(
          groupedMetaData,
          formik,
          key,
          conditionalFields,
          hiddenFields
        );

        const groupBorderMatch =
          groupsWithBorders &&
          groupsWithBorders.filter((item) => item.groupName === key).length === 1
            ? groupsWithBorders.filter((item) => item.groupName === key)[0]
            : undefined;

        const groupBorderColour = groupBorderMatch ? groupBorderMatch.groupBorderColour : null;

        const parsedKey =
          key && typeof key === "string" ? key.replaceAll(/\s/g, "").toLowerCase() : null;

        const column =
          parsedKey && groupColumns && groupColumns.hasOwnProperty(parsedKey)
            ? groupColumns[parsedKey as keyof typeof groupColumns]
            : 1;

        const subColumns: number | null =
          groupSubColumns && groupSubColumns.hasOwnProperty(key) ? groupSubColumns[key] : null;

        const hideSpecificGroupLabel =
          hideSpecificGroupLabels && hideSpecificGroupLabels.includes(key) ? true : false;

        const showSpecificGroupLabel =
          !hideGroupLabels || (hideGroupLabels && !hideGroupLabels.includes(key));

        const hideEntireGroup = hiddenGroups && hiddenGroups.includes(key);

        const hideGroupLabel =
          (hideSpecificGroupLabels && hideSpecificGroupLabels.includes(key)) ||
          key === null ||
          !key;

        if (column === columnNumber && !hideEntireGroup) {
          return (
            <React.Fragment key={`fragment-container-${_i}`}>
              <div
                className={`Group ${
                  key
                    ? key && typeof key === "string"
                      ? key.replaceAll(/\s/g, "")
                      : ""
                    : "Details"
                }
                ${subColumns ? `GroupSubColumns${subColumns}` : ""}
                ${subColumns && !hideSpecificGroupLabels ? `GroupSubColumns` : ""}
                `}
                style={
                  groupBorderColour
                    ? {
                        border: `1px solid ${groupBorderColour}`,
                        borderRadius: "3px",
                        padding: "10px",
                      }
                    : {}
                }
                key={`group-${_i}`}
              >
                {key !== "undefined" &&
                key !== "null" &&
                !hideAllGroupLabels &&
                !hideViewGroupLabel &&
                !hideSpecificGroupLabel &&
                !hideGroupLabel ? (
                  <h4
                    className={`SubTitle ${
                      key && typeof key === "string" ? key.replaceAll(/\s/g, "") : ""
                    } ${subColumns && !hideSpecificGroupLabels ? "GroupSubColumns" : ""}
                     ${!showSpecificGroupLabel ? "Hide" : ""}`}
                    key={`subtitle-${_i}`}
                  >
                    {key}
                  </h4>
                ) : (
                  !hideViewGroupLabel &&
                  !hideGroupLabel && (
                    <h4 className="SubTitle Details" key={`subtitle-2-${_i}`}>
                      Details
                    </h4>
                  )
                )}
                <FormBuilder
                  data={{
                    ...data,
                    metadata: groupedMetaData[key],
                  }}
                  loadingFallback={<Spinner positionAbsolute />}
                  readOnly
                  fieldRenderers={readOnlyRenderers}
                  hiddenFields={fieldsToHide as any}
                  key={`form-builder-${_i}`}
                  overrideFormFieldRenderer={overrideFormReadOnlyFieldRenderer}
                />
              </div>
            </React.Fragment>
          );
        } else {
          return null;
        }
      })}
    </Fragment>
  );
}

type RenderCreateUpdateGroupProps<Data, Fields extends object> = {
  groupedMetaData: _.Dictionary<FormField<Fields>[]>;
  conditionalFields: ConditionalFields | undefined;
  hiddenFields: string[] | undefined;
  hiddenGroups: string[] | undefined;
  data: InitialDataResponse<Fields>;
  fieldRenderers: ((data: any, refs?: any) => FieldRenderers<Fields>) | undefined;
  overrideFormFieldRenderer: FormFieldRendererOverride<Fields> | undefined;
  formik: FormikProps<any>;
  groupColumns: GroupColumns | undefined;
  columnNumber: number;
  groupSubColumns?: { [groupName: string]: number };
  hideAllGroupLabels?: boolean;
  hideSpecificGroupLabels?: string[];

  hideGroupLabel?: boolean;
  hideGroupLabels?: string[];
  refs?: { selectRef?: any; containerRef?: any };
  groupsWithBorders?: GroupsWithBorders;
};

function RenderCreateUpdateGroup<Data, Fields extends object>({
  groupedMetaData,
  conditionalFields,
  hiddenFields,
  hiddenGroups,
  data,
  fieldRenderers,
  overrideFormFieldRenderer,
  formik,
  groupColumns,
  columnNumber,
  groupSubColumns,
  hideAllGroupLabels = false,
  hideSpecificGroupLabels,

  hideGroupLabel = false,
  hideGroupLabels,
  refs,
  groupsWithBorders,
}: RenderCreateUpdateGroupProps<Data, Fields>): JSX.Element {
  // Set read only field values on related form:
  // (This should use formik.values, not original finalOriginFormData)

  return (
    <Fragment>
      {Object.keys(groupedMetaData).map((key, _i) => {
        let [filteredGroupedMetaData, fieldsToHide] = filterGroupedMetaData(
          groupedMetaData,
          formik,
          key,
          conditionalFields,
          hiddenFields
        );

        const dataLength = filteredGroupedMetaData.length;

        const parsedKey =
          key && typeof key === "string" ? key.replaceAll(/\s/g, "").toLowerCase() : null;

        const column =
          parsedKey && groupColumns && groupColumns.hasOwnProperty(parsedKey)
            ? groupColumns[parsedKey as keyof typeof groupColumns]
            : 1;

        const subColumns: number | null =
          groupSubColumns && groupSubColumns.hasOwnProperty(key) ? groupSubColumns[key] : null;

        const hideSpecificGroupLabel =
          hideSpecificGroupLabels && hideSpecificGroupLabels.includes(key) ? true : false;

        const groupBorderMatch =
          groupsWithBorders &&
          groupsWithBorders.filter((item) => item.groupName === key).length === 1
            ? groupsWithBorders.filter((item) => item.groupName === key)[0]
            : undefined;

        const groupBorderColour = groupBorderMatch ? groupBorderMatch.groupBorderColour : null;

        const showSpecificGroupLabel =
          !hideGroupLabels || (hideGroupLabels && !hideGroupLabels.includes(key));

        const hideEntireGroup = hiddenGroups && hiddenGroups.includes(key);

        if (column === columnNumber && !hideEntireGroup) {
          return (
            <Fragment key={`fragment-container${_i}`}>
              <div
                className={`Group ${
                  key !== "undefined" && key !== "null"
                    ? key && typeof key === "string"
                      ? key.replaceAll(/\s/g, "")
                      : ""
                    : "Default"
                }
               Column${column}
               ${subColumns ? `GroupSubColumns${subColumns}` : ""}
               ${subColumns && !hideSpecificGroupLabels ? `GroupSubColumns` : ""}
               ${dataLength === 0 ? "NoFields" : ""}
              `}
                style={
                  groupBorderColour
                    ? {
                        border: `1px solid ${groupBorderColour}`,
                        borderRadius: "3px",
                        padding: "10px",
                      }
                    : {}
                }
                key={`group-${_i}`}
              >
                {key !== "undefined" &&
                  key !== "null" &&
                  !hideAllGroupLabels &&
                  !hideSpecificGroupLabel && (
                    <h4
                      className={`SubTitle ${
                        key && typeof key === "string" ? key.replaceAll(/\s/g, "") : ""
                      } ${
                        subColumns && !hideSpecificGroupLabels ? "GroupSubColumns" : ""
                      } ${!showSpecificGroupLabel ? "Hide" : ""}`}
                      key={`subtitle-${_i}`}
                    >
                      {key}
                    </h4>
                  )}
                <FormElements
                  values={formik.values}
                  data={{
                    ...data,
                    metadata: groupedMetaData[key],
                  }}
                  loadingFallback={<Spinner positionAbsolute />}
                  fieldRenderers={fieldRenderers ? fieldRenderers(data, refs) : undefined}
                  overrideFormFieldRenderer={overrideFormFieldRenderer}
                  hiddenFields={fieldsToHide as any}
                  key={`form-elements-${_i}`}
                />
              </div>
            </Fragment>
          );
        } else {
          return <Fragment key={_i}></Fragment>;
        }
      })}
    </Fragment>
  );
}

const SYSTEM_DETAILS_KEYS = ["adddate", "addedby", "editdate", "lasteditedby"];

export const deleteUncompletedItems = async (onDone?: () => void) => {
  let allIdsDeletedSuccessfully = true;

  const response = await api.get(endpoints.sites.deleteIncompleteItems);

  if (response.status === 200 && response.data.status === `1`) {
  } else {
    allIdsDeletedSuccessfully = false;
  }

  if (allIdsDeletedSuccessfully) {
    toast(
      <ToastMessage
        toastData={[{ title: "Success", text: "Deleted incomplete data" }]}
        className="ToastSuccess"
      />,
      {
        ...toastConfig,
      }
    );
  } else {
    toast(
      <ToastMessage
        toastData={[
          {
            title: "Error",
            text: "Failed to delete incomplete data",
          },
        ]}
        className="ToastError"
      />,
      {
        ...toastConfig,
      }
    );
  }
  if (onDone) {
    onDone();
  }
};
